import { defineStore } from 'pinia';
import { useUiStore } from '@/store/ui';
import { useCalendarStore } from '@/store/calendar';
import { geocodeAddress, geocodeAddressReverse } from '../services/geocoder';
import filters from '@/filters/index';
import dayjs from 'dayjs';

export const useHotelsStore = defineStore('hotels', {
    state: () => ({
        allGroups: [],
        autoSuggestQuery: null,
        currentPage: 1,
        favSearchQuery: null,
        filteredSearchResults: [],
        groupFavsBy: null,
        groups: [],
        infoWindowContent: {},
        loading: true,
        mapLoading: false,
        mapSearchQuery: null,
        mapType: 'search',
        perPage: 10,
        pointsProgram: {},
        property: {},
        propertyName: null,
        searchCompleted: false,
        searchDistance: 40,
        searchLatitude: null,
        searchLocation: null,
        searchLongitude: null,
        searchQuery: null,
        searchResults: {
            data: [],
            total: 0,
        },
        searchResultsCount: 0,
        selectedGroups: [],
        showInfoWindow: false,
        showMap: false,
        showOnMap: false,
        slug: null,
        sort: null,
        stayLength: null,
        usersFavHotels: [],
        usersRecentSearches: [],
    }),

    getters: {
        startDate() {
            const calendarStore = useCalendarStore();
            return calendarStore.startDate;
        },
        endDate() {
            const calendarStore = useCalendarStore();
            return calendarStore.endDate;
        },
        mapCenter() {
            if (this.property) {
                return {
                    lat: parseFloat(this.property.latitude),
                    lng: parseFloat(this.property.longitude),
                };
            }
        },
        calConfig() {
            return {
                height: 800,
                date: new Date(),
                modes: {
                    year: {
                        showEvents: 'dots',
                    },
                },
                panel: false,
                //sidebar: null,
                showEvents: 'dots',
                crudManager: {
                    loadUrl: '/api/hotels/properties/',
                    //autoLoad : true
                },
            };
        },
        mapMarkers() {
            let arr = [];
            this.filteredAndPaginatedSearchResults.forEach((i) => {
                arr.push([
                    i.name,
                    parseFloat(i.latitude),
                    parseFloat(i.longitude),
                    i,
                ]);
            });
            return arr;
        },
        favsMapMarkers() {
            let arr = [];
            const flattenedFavs = Object.values(this.filteredFavs).flat();
            flattenedFavs.forEach((fav) => {
                arr.push([
                    fav.name,
                    parseFloat(fav.latitude),
                    parseFloat(fav.longitude),
                    fav,
                ]);
            });
            return arr;
        },
        filteredFavs(state) {
            let filtered = Object.keys(state.usersFavHotels).map((key) => {
                if (state.selectedGroups.length > 0) {
                    return state.usersFavHotels[key].filter((hotel) => {
                        return state.selectedGroups.includes(hotel.group);
                    });
                }
                return state.usersFavHotels[key];
            });

            return filtered;
        },
        filteredAndPaginatedSearchResults(state) {
            this.applyFilters(state.searchResults.data);
            const paginated = this.applyPagination(state.filteredSearchResults);
            return paginated;
        },
        groupsFilters() {
            // looks through searchResults for hotel groups represented in the results (so that hotel brand select dropdown only shows groups that exist in the results)
            const groups = this.searchResults.data.reduce((acc, item) => {
                const exists = acc.find((g) => g.slug === item.group.slug);
                if (!exists) {
                    acc.push({
                        ...item.group,
                        image: item.propertyProgram?.image,
                    });
                }
                return acc;
            }, []);
            return groups;
        },
        startRange: (state) => {
            return (state.currentPage - 1) * state.perPage + 1;
        },
        endRange: (state) => {
            const end = state.currentPage * state.perPage;
            return end > state.filteredSearchResults.length ? state.filteredSearchResults.length : end;
        },
        totalPages: (state) => {
            return Math.ceil(state.filteredSearchResults.length / state.perPage);
        },
    },
    actions: {
        applyFilters() {
            const filtered = this.searchResults.data.filter((item) => {
                let propertyNameFilter = true;
                let group = true;

                if (this.propertyName && this.propertyName.length > 1) {
                    propertyNameFilter = item.name
                        .toLowerCase()
                        .includes(this.propertyName);
                }
                if (this.selectedGroups.length > 0) {
                    group = this.selectedGroups.includes(item.group.slug);
                }

                return propertyNameFilter && group;
            });

            // this.setResultsCount(filtered.length);
            this.setFilteredSearchResults(filtered);
        },

        applyPagination(arr) {
            const startIndex = (this.currentPage - 1) * this.perPage;
            const endIndex = startIndex + this.perPage;

            const paginated = arr.slice(startIndex, endIndex);

            const sorted = this.applySort(paginated, this.sort);
            return sorted;
        },
        applySort(arr, sortValue) {
            let sorted;
            if (sortValue === 'distance' || sortValue === '-distance') {
                sorted = this.sortByDistance(arr, this.searchLocation);
                if (sortValue.includes('-')) {
                    sorted = sorted.reverse();
                }
            } else {
                sorted = arr.sort((a, b) => {
                    if (a[sortValue] < b[sortValue]) {
                        return sortValue.includes('min') ? -1 : 1;
                    }
                    if (a[sortValue] > b[sortValue]) {
                        return sortValue.includes('min') ? 1 : -1;
                    }
                    return 0;
                });
            }
            return sorted;
        },
        async assignProgram() {
            this.loading = true;
            try {
                const program = await this.getSelectedProgramInfo(
                    this.property.group
                );
                this.setPointsProgram(program);
            } catch (error) {
                console.error('Error retrieving property program:', error);
            } finally {
                this.loading = false;
            }
        },
        generateSearchUrl(searchData) {
            const baseUrl = '/hotels/search';

            const { search, start_date, end_date } = searchData;

            const url = `${baseUrl}?destination=${encodeURIComponent(
                search
            )}&start_date=${encodeURIComponent(
                start_date
            )}&end_date=${encodeURIComponent(end_date)}`;

            return url;
        },
        setRecentSearches(val) {
            this.usersRecentSearches = val;
        },
        async getAllGroups() {
            try {
                const response = await ApiService.apiCall(
                    '/hotels/groups/',
                    'get'
                );
                if (response.data) {
                    this.setAllGroups(response.data.data);
                } else {
                    throw new Error(response.message);
                }
            } catch (error) {
                console.error(error);
            }
        },
        async getRecentSearches() {
            try {
                const response = await ApiService.apiCall(
                    '/hotels/search-log',
                    'post'
                );
                if (response.data) {
                    const searchesWithUrls = response.data.map((searchObj) => {
                        searchObj['params'] = JSON.parse(searchObj.params);
                        searchObj['url'] = this.generateSearchUrl(
                            searchObj.params
                        );
                        return searchObj;
                    });
                    this.setRecentSearches(searchesWithUrls);
                } else {
                    throw new Error(
                        'Unable to retrieve recent searches for this user.'
                    );
                }
            } catch (error) {
                this.setRecentSearches([]);
                console.error(error);
            }
        },
        async getCoordinates(val) {
            const coordinates = await geocodeAddress(val);

            if (coordinates) {
                return {
                    lat: coordinates.latitude,
                    lng: coordinates.longitude,
                };
            }
            return null;
        },
        async getProperty(slug) {
            this.setLoading(true);
            this.slug = slug;

            return ApiService.apiCall('/hotels/properties/' + slug, 'get')
                .then((response) => {
                    this.property = response.data.property;

                    this.assignProgram();
                    setTimeout(() => {
                        this.setLoading(false);
                    }, 500);
                    return response.data;
                })
                .catch(() => {
                    this.setLoading(false);
                });
        },
        async getSelectedProgramInfo(groupName) {
            try {
                const program = await ApiService.apiCall(
                    `/program-search?propertyName=${groupName}`,
                    'get'
                );
                return program.data;
            } catch (error) {
                this.setLoading(false);
                console.error(error);
            } finally {
                this.setLoading(false);
            }
        },
        getStayLength() {
            const start = dayjs(this.startDate);
            const end = dayjs(this.endDate);
            this.stayLength = end.diff(start, 'day');
        },
        async getUsersFavHotels() {
            try {
                this.setLoading(true);
                const params = {
                    search: this.favSearchQuery,
                    sort: this.sort,
                    groupBy: this.groupFavsBy,
                    // group: this.selectedGroups.join(','),
                };
                const response = await ApiService.apiCall(
                    `/hotels/get-favs`,
                    'get',
                    params
                );
                if (response.data) {
                    this.usersFavHotels = response.data;
                } else {
                    throw new Error('Unable to fetch user favorites');
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.setLoading(false);
            }
        },
        removeFromFavorites(propertyId) {
            const filteredHotels = Object.keys(this.usersFavHotels).reduce(
                (newFavsObj, curr) => {
                    const filteredProperties = this.usersFavHotels[curr].filter(
                        (p) => p.id !== propertyId
                    );
                    newFavsObj[curr] = filteredProperties;
                    return newFavsObj;
                },
                {}
            );

            this.usersFavHotels = filteredHotels;
        },
        setMapLoading(val) {
            this.mapLoading = val;
        },

        async search() {
            const uiStore = useUiStore();
            this.property = {};
            this.setLoading(true);
            this.searchCompleted = false;

            this.getStayLength();

            const data = {
                search: this.searchQuery,
                start_date: this.startDate,
                end_date: this.endDate,
                include: 'availability',
                page: uiStore.currentPage,
                all: false,
                brand: this.selectedGroups.join(','),
                latitude: this.searchLocation?.lat,
                longitude: this.searchLocation?.lng,
                distance: this.searchDistance,
                limit: uiStore.searchResultsLimit,
            };

            try {
                const response = await ApiService.apiCall(
                    `/hotels/search`,
                    'post',
                    data
                );
                this.setAutosuggestQuery(null);
                this.searchResults.data = response.data.data;
                this.searchResults.total = response.data.meta.total;
                this.applyFilters(response.data.data);

                if (this.searchQuery) {
                    this.searchLocation = this.getCoordinates(this.searchQuery);
                }

                if (import.meta.env.VITE_APP_ENV === 'production') {
                    woopra.track('property-search', {
                        querystring: this.searchQuery,
                        startdate: this.startDate,
                        enddate: this.endDate,
                        uuid: localStorage.getItem('uuid'),
                    });
                }

                setTimeout(() => {
                    this.searchCompleted = true;
                    this.setLoading(false);
                }, 500);
            } catch (error) {
                console.error('Error during search:', error);
                this.setLoading(false);
            }
        },
        setSearchDistance(num) {
            this.searchDistance = num;
        },
        setGroupFavsBy(val) {
            this.groupFavsBy = val;
        },
        setMapType(val) {
            this.mapType = val;
        },
        setPointsProgram(newVal) {
            this.pointsProgram = newVal;
        },
        setShowInfoWindow(val) {
            this.showInfoWindow = val;
        },
        setShowOnMap(val) {
            this.showOnMap = val;
            this.showMap = val;
        },
        setFavSearchQuery(val) {
            this.favSearchQuery = val;
        },
        setAllGroups(groups) {
            this.allGroups = groups;
        },
        setGroups(groupName, origin = 'favorites') {
            this.setLoading(true);
            const index = this.selectedGroups.indexOf(groupName);
            if (index >= 0) {
                this.selectedGroups.splice(index, 1);
            } else {
                this.selectedGroups.push(groupName);
            }
            setTimeout(() => {
                this.setLoading(false);
            }, 500);

            if (origin === 'property-search') {
                this.applyFilters(this.filteredSearchResults);
            }

            if (
                Object.keys(this.usersFavHotels).length &&
                origin === 'favorites'
            ) {
                this.setGroupFavsBy(null);
                this.getUsersFavHotels();
            }
        },
        resetDates() {
            const calendarStore = useCalendarStore();
            calendarStore.setStartDate('');
            calendarStore.setEndDate('');
        },
        clearSelectedGroups() {
            this.selectedGroups = [];
        },
        clearHotelFilters() {
            this.clearSelectedGroups();
            this.resetDates();
            this.setSearchQuery(null);
            this.setPropertyName(null);
            this.setAutosuggestQuery(null);
            this.setCurrentPage(1);
            this.searchCompleted = false;
            this.applyFilters();
        },
        setInfoWindowContent(val) {
            this.infoWindowContent = val;
        },
        setSearchLocation(obj) {
            this.searchLocation = obj;
        },
        setLatLong(obj) {
            this.searchLatitude = obj.lat();
            this.searchLongitude = obj.lng();
        },
        setLoading(val) {
            this.loading = val;
        },
        setCurrentPage(page) {
            this.currentPage = page;
        },
        setSearchQuery(val) {
            this.page = 1;
            this.searchQuery = val;
        },
        setAutosuggestQuery(val) {
            this.autoSuggestQuery = val;
        },
        setMapSearchQuery(val) {
            this.mapSearchQuery = val;
        },
        sortProperties(value, action) {
            const uiStore = useUiStore();
            uiStore.setSort(value);
            if (action === 'getFavorites') {
                this.getUsersFavHotels();
            }
        },
        sortByDistance(locations, origin) {
            return locations.sort((a, b) => {
                const distanceA = filters.haversineDistance(origin, {
                    lat: a.latitude,
                    lng: a.longitude,
                });
                const distanceB = filters.haversineDistance(origin, {
                    lat: b.latitude,
                    lng: b.longitude,
                });
                return distanceA - distanceB;
            });
        },
        setFilteredSearchResults(arr) {
            this.filteredSearchResults = arr;
        },
        toggleLargeMap() {
            this.showMap = !this.showMap;
            // if (this.showMap) this.loadLargeMap(propertyId, mapCenter);
        },
        toggleToolFavorite(item, indie = false) {
            const method = !item.isFavorite ? 'post' : 'delete';
            if (
                this.usersFavHotels.length === 1 &&
                method === 'delete' &&
                this.page > 1
            ) {
                this.page--;
            }

            ApiService.apiCall(`/hotels/${item.id}/fav`, method)
                .then((res) => {
                    if (!this.property.id) {
                        if (!indie) {
                            // this.getUsersFavTools(); //need to figure out if needed
                        }
                        const index = this.searchResults.data.findIndex(
                            (prop) => prop.id === item.id
                        );
                        if (index !== -1) {
                            this.searchResults.data[index].isFavorite =
                                !item.isFavorite;
                        }
                    } else {
                        this.property.isFavorite = !this.property.isFavorite;
                    }
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('property-favorite', {
                            action:
                                method === 'post'
                                    ? 'favorited'
                                    : 'removed favorite',
                            propertyname: item.name,
                            id: item.id,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }

                    if (item.isFavorite) {
                        setTimeout(() => {
                            this.removeFromFavorites(item.id);
                        }, 300);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        setProperty(property) {
            this.property = property;
        },
        setPropertyName(name) {
            this.propertyName = name;
        },
        setSearchCompleted(val) {
            this.searchCompleted = val;
        },
        clearParams() {
            this.loading = false;
            this.searchQuery = null;
            //this.startDate = null;
            //this.endDate = null;
            //this.selectedGroups = [];
        },
        setPerPage(perPage) {
            this.perPage = perPage;
        },
    },
});
